import { useParams } from 'react-router-dom'

import { Box, BoxList, Button, Text } from '@factorialco/deprecated-design-system'
import type { Currency } from 'dinero.js'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import ShowStatus from 'modules/payroll_bundle/components/common/components/ShowStatus'
import { getConceptByName, useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { DEFAULT_RESULT_CONCEPT, resultConceptsLabels } from 'modules/payroll_bundle/lib/constants'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'
import { paymentItemLib } from 'modules/payroll_bundle/lib/paymentItem'
import {
  ResultRowData,
  sumResultRowsDataByConcept,
} from 'modules/payroll_bundle/lib/resultsTableData'
import {
  type Payments,
  paymentItemConcept,
} from 'modules/payroll_bundle/store/collections/payments'

interface Props {
  employeeResultRowsData: ResultRowData[]
  employeeId: number
  payments: Payments
  currentPolicyPeriodId: number
  currency: Currency
}

const PaymentsTab = ({
  employeeResultRowsData,
  payments,
  currentPolicyPeriodId,
  employeeId,
  currency,
}: Props) => {
  const paymentTypeLabels = resultConceptsLabels()
  const { navigation } = useNavigation()
  const { concepts } = useConcepts()

  const { policyId: paramsPolicyId } = useParams()
  const policyId = Number(paramsPolicyId)

  const paymentItems = payments.models.flatMap((payment) =>
    payment.employeePaymentItems(employeeId)
  )
  const netSalaryConcept = getConceptByName(concepts, DEFAULT_RESULT_CONCEPT.net_pay)
  const hasPayments = paymentItems.length > 0

  if (!hasPayments) {
    return (
      <Box width="full" padding="s32" alignItems="center" gap="s8">
        <Text.Normal>{i18n.t('payroll_bundle.results_payments.payments.no_payments')}</Text.Normal>
        <Button.Router
          to={navigation.payrollBundle.policy.period.cycles.payments.fullPath({
            policyId,
            periodId: Number(currentPolicyPeriodId),
          })}
        >
          {i18n.t('payroll_bundle.results_payments.results.side_panel.go_to_payments')}
        </Button.Router>
      </Box>
    )
  }

  const totalPaymentsAmountInCents = paymentItemLib.sumPaymentItems(paymentItems)
  const totalPaidAmountInCents = paymentItemLib.sumPaymentItems(
    paymentItemLib.getPaidPaymentItems(paymentItems, payments)
  )
  const pendingToPayAmountInCents =
    netSalaryConcept === null
      ? 0
      : sumResultRowsDataByConcept(
          employeeResultRowsData,
          netSalaryConcept.id,
          currentPolicyPeriodId
        ) - totalPaidAmountInCents

  return (
    <Box>
      <Box
        flexDirection="row"
        alignItems="center"
        gap="s24"
        padding="s24"
        border={{
          bottom: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
      >
        <Box justifyContent="center" gap="s2">
          <Text.Normal size="200" color="grey800">
            {i18n.t('payroll_bundle.results_payments.results.side_panel.included')}
          </Text.Normal>
          <Text.Normal weight="bold">
            {formatAmount(totalPaymentsAmountInCents, currency)}
          </Text.Normal>
        </Box>
        <Box justifyContent="center" gap="s2">
          {pendingToPayAmountInCents > 0 && (
            <>
              <Text.Normal size="200" color="grey800">
                {i18n.t('payroll_bundle.results_payments.results.side_panel.still_pending')}
              </Text.Normal>
              <Text.Normal weight="bold">
                {formatAmount(pendingToPayAmountInCents, currency)}
              </Text.Normal>
            </>
          )}
        </Box>
      </Box>
      <Box gap="s16" padding="s24">
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t('payroll_bundle.results_payments.results.side_panel.payment_in_period', {
            paymentCount: paymentItems.length,
          })}
        </Text.Normal>
        <BoxList>
          {paymentItems.map((paymentItem) => {
            const paymentStatus = paymentItemLib.getPaymentStatus(paymentItem, payments)
            const concept = paymentItemConcept(paymentItem)

            return (
              <BoxList.Item key={paymentItem.id}>
                <Box justifyContent="spaceBetween" alignItems="center" width="full" gap="s8">
                  <Box flexDirection="row" justifyContent="spaceBetween" width="full">
                    <Text.Normal weight="semibold" color="grey800">
                      {paymentItemLib.getAmountInCentsFormatted(paymentItem, currency)}
                    </Text.Normal>
                    {paymentStatus && <ShowStatus status={paymentStatus} />}
                  </Box>
                  <Box flexDirection="row" width="full">
                    <Text.Normal color="grey800">
                      {concept ? paymentTypeLabels[concept] : ''} ·{' '}
                      {moment(paymentItemLib.getPaymentCreatedAt(paymentItem, payments)).format(
                        'MMM D h:mm a'
                      )}
                    </Text.Normal>
                  </Box>
                </Box>
              </BoxList.Item>
            )
          })}
        </BoxList>
      </Box>
      <Box paddingX="s24">
        <Button.Router
          to={navigation.payrollBundle.policy.period.cycles.payments.fullPath({
            policyId,
            periodId: Number(currentPolicyPeriodId),
          })}
        >
          {i18n.t('payroll_bundle.results_payments.results.side_panel.go_to_payments')}
        </Button.Router>
      </Box>
    </Box>
  )
}

export default PaymentsTab
